const STORAGE_API_BASE_URL_KEY = 'tiempos_api_base_url';

const DEFAULT_API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL || '/api');

type ConexionResultado = {
  ok: boolean;
  error?: string;
};

function limpiarFinalSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function normalizarPath(pathname: string): string {
  const limpio = pathname.replace(/\/+/g, '/').replace(/\/+$/, '');
  return limpio || '';
}

function asegurarApiPath(path: string): string {
  if (!path || path === '/') return '/api';
  if (path === '/api') return '/api';
  return path;
}

export function normalizeApiBaseUrl(rawUrl: string): string {
  const input = rawUrl.trim();
  if (!input) return '';

  if (input.startsWith('/')) {
    const path = asegurarApiPath(normalizarPath(input));
    return limpiarFinalSlash(path);
  }

  try {
    const url = new URL(input);
    const path = asegurarApiPath(normalizarPath(url.pathname));
    return `${url.origin}${path}`;
  } catch {
    return '';
  }
}

export function validateApiBaseUrl(rawUrl: string): { ok: boolean; normalized?: string; error?: string } {
  const input = rawUrl.trim();
  if (!input) {
    return { ok: false, error: 'La URL del servidor es obligatoria.' };
  }

  if (!input.startsWith('/') && !/^https?:\/\//i.test(input)) {
    return { ok: false, error: 'La URL debe iniciar con http:// o https://.' };
  }

  if (input.startsWith('/')) {
    const normalized = normalizeApiBaseUrl(input);
    if (!normalized) {
      return { ok: false, error: 'La URL ingresada no es válida.' };
    }
    return { ok: true, normalized };
  }

  try {
    const parsed = new URL(input);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return { ok: false, error: 'Solo se permiten URLs http o https.' };
    }
  } catch {
    return { ok: false, error: 'La URL ingresada no es válida.' };
  }

  const normalized = normalizeApiBaseUrl(input);
  if (!normalized) {
    return { ok: false, error: 'La URL ingresada no es válida.' };
  }

  return { ok: true, normalized };
}

export function getApiBaseUrl(): string {
  const saved = localStorage.getItem(STORAGE_API_BASE_URL_KEY);
  if (saved && saved.trim()) {
    return saved;
  }
  return DEFAULT_API_BASE_URL;
}

export function getServerBaseUrl(): string {
  const apiBase = getApiBaseUrl();
  if (apiBase.endsWith('/api')) {
    return apiBase.slice(0, -4);
  }
  return apiBase;
}

export function hasApiBaseUrlConfigurada(): boolean {
  const saved = localStorage.getItem(STORAGE_API_BASE_URL_KEY);
  if (saved && saved.trim()) return true;
  // If a build-time default URL exists, treat as configured so the app
  // goes straight to login instead of forcing the server selector.
  return Boolean(DEFAULT_API_BASE_URL && DEFAULT_API_BASE_URL !== '/api');
}

export function setApiBaseUrl(url: string): string {
  const validacion = validateApiBaseUrl(url);
  if (!validacion.ok || !validacion.normalized) {
    throw new Error(validacion.error || 'URL inválida.');
  }
  localStorage.setItem(STORAGE_API_BASE_URL_KEY, validacion.normalized);
  return validacion.normalized;
}

export function clearApiBaseUrl(): void {
  localStorage.removeItem(STORAGE_API_BASE_URL_KEY);
}

export function buildApiUrl(endpoint: string): string {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${getApiBaseUrl()}${cleanEndpoint}`;
}

export function buildServerUrl(path = ''): string {
  const cleanPath = path
    ? path.startsWith('/')
      ? path
      : `/${path}`
    : '';
  return `${getServerBaseUrl()}${cleanPath}`;
}

async function fetchConTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      method: 'GET',
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

function esRespuestaConectada(status: number): boolean {
  return (status >= 200 && status < 300) || status === 401 || status === 403 || status === 405;
}

export async function probarConexionServidor(rawUrl?: string): Promise<ConexionResultado> {
  let apiBase: string;
  if (rawUrl) {
    const validacion = validateApiBaseUrl(rawUrl);
    if (!validacion.ok || !validacion.normalized) {
      return { ok: false, error: validacion.error || 'URL inválida.' };
    }
    apiBase = validacion.normalized;
  } else {
    apiBase = getApiBaseUrl();
  }

  const serverBase = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
  const candidatos = [
    `${serverBase}/health`,
    `${serverBase}/openapi.json`,
    `${serverBase}/docs`,
    `${apiBase}/auth/me`,
  ];

  for (const url of candidatos) {
    try {
      const response = await fetchConTimeout(url, 4500);
      if (esRespuestaConectada(response.status)) {
        return { ok: true };
      }
    } catch {
      // Intentar siguiente endpoint
    }
  }

  return { ok: false, error: 'No se pudo conectar con el servidor seleccionado' };
}

export { STORAGE_API_BASE_URL_KEY, DEFAULT_API_BASE_URL };